<template>
    <div v-if="loading" class="w-full flex flex-col box-border mt-4" >
        <div class="skeleton-loader w-full mt-1 h-[50px]"></div>
        <div class="skeleton-loader w-[80%] mt-1 h-[30px]"></div>
        <div class="skeleton-loader w-[40%] mt-1 h-[30px]"></div>
        <div class="skeleton-loader w-[60%] mt-1 h-[30px]"></div>
        <div class="skeleton-loader w-[50%] mt-1 h-[30px]"></div>
        <div class="skeleton-loader w-[80%] mt-4 h-[30px]"></div>
        <div class="skeleton-loader w-[40%] mt-1 h-[30px]"></div>
        <div class="skeleton-loader w-[60%] mt-1 h-[30px]"></div>
        <div class="skeleton-loader w-[50%] mt-1 h-[30px]"></div>
    </div>
    <div v-else class="w-full mt-4 border-default-4/50 border p-4 box-border">
        <div v-if="documentType === 'image'">
            <img :src="useImagePathFormatter(organization.slug, document.documentable.filename)" alt="Image" class="w-full" />
        </div>
        <div v-if="documentType === 'pdf'" class="w-full xxs:overflow-hidden lg:overflow-auto" ref="pdfContainer">
            <PDFControls
                v-if="isPDFLoaded"
                :currentPagePDF="currentPagePDF"
                :totalPagesPDF="totalPagesPDF"
                :showAllPages="showAllPages"
                :scalePDF="scalePDF"
                @update:currentPagePDF="currentPagePDF = $event"
                @update:showAllPages="showAllPages = !showAllPages"
                @update:scalePDF="scalePDF = $event"
                @previousPage="currentPagePDF--"
                @nextPage="currentPagePDF++"
            />
            <div v-if="!isPDFLoaded" class="w-full flex items-center justify-center">
                Loading PDF...
            </div>
            <vue-pdf-embed
                :source="useImagePathFormatter(organization.slug, document.documentable.filename)"
                :page="currentPagePDF"
                @password-requested="handlePdfPasswordRequest"
                @loaded="pdfInit"
                @rendered="isPdfReady"
                :width="pdfWidth"
            />
            <PDFControls
                v-if="isPDFLoaded"
                :currentPagePDF="currentPagePDF"
                :totalPagesPDF="totalPagesPDF"
                :showAllPages="showAllPages"
                :scalePDF="scalePDF"
                @update:currentPagePDF="currentPagePDF = $event"
                @update:showAllPages="showAllPages = $event"
                @update:scalePDF="scalePDF = $event"
                @previousPage="currentPagePDF--"
                @nextPage="currentPagePDF++"
            />
        </div>
        <div v-if="documentType === 'word'">
            <div v-html="docxContent"></div>
        </div>
        <div v-if="documentType === 'excel'" class="overflow-x-scroll max-w-fit text-body-6 text-left">
            <table v-if="excelData.length > 0" class="border border-default-4/80 border-collapse w-full">
                <thead>
                <tr>
                    <th v-for="(header, index) in excelData[0]" :key="index" class="border border-default-4/80 px-2 py-1">
                        {{ header }}
                    </th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(row, rowIndex) in excelData.slice(1)" :key="rowIndex">
                    <td
                        v-for="(cell, cellIndex) in row"
                        :key="cellIndex"
                        class="border border-default-4/80 px-2 py-1 min-w-[80px]"
                        :class="cellIndex % 2 === 0 && 'bg-black/5'"
                    >
                        {{ cell }}
                    </td>
                </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script setup>
import {computed, onMounted, watch} from "vue";
import {useImagePathFormatter} from "@/composables/useImagePathFormatter";
import {useAuth} from "@/stores/AuthStore";
import {storeToRefs} from "pinia";
import { ref } from 'vue';
import * as mammoth from 'mammoth';
import {toast} from "vue3-toastify";
import * as XLSX from 'xlsx';
import VuePdfEmbed from 'vue-pdf-embed'
import PDFControls from "@/components/custom/documents/PDFControls.vue";

const {organization} = storeToRefs(useAuth());

const props = defineProps({
    document: {
        type: Object,
        required: true,
    }
})

const scalePDF = ref(100)
const currentPagePDF = ref(1)
const totalPagesPDF = ref(0)
const isPDFLoaded = ref(false)
const showAllPages = ref(false);
const pdfContainer = ref(null)

const docxContent = ref('');
const excelData = ref([]);
const loading = ref(false);

const documentType = computed(() => {
    switch(props.document.documentable.mime_type) {
    case 'application/pdf':
        return 'pdf';
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        return 'excel';
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return 'word';
    case 'image/png':
    case 'image/jpeg':
    default:
        return 'image';
    }
})

async function fetchWord() {
    loading.value = true;

    let response, arrayBuffer

    try {
        response = useImagePathFormatter(organization.value.slug, props.document.documentable.filename);
        response = await fetch(response);
        arrayBuffer = await response.arrayBuffer();
    } catch (e) {
        toast.error('Error fetching the word file');
    }

    try {
        const result = await mammoth.convertToHtml({arrayBuffer});
        docxContent.value = result.value; // Set the HTML content
    } catch (error) {
        toast.error('Error parsing the word file');
    } finally {
        loading.value = false;
    }
}

async function fetchAndParseXlsx() {
    let response
    loading.value = true;

    try {
        response = useImagePathFormatter(organization.value.slug, props.document.documentable.filename);
        response = await fetch(response);
    } catch (e) {
        toast.error('Error fetching the excel file');
    }

    try {
        // Read the response as an ArrayBuffer
        const arrayBuffer = await response.arrayBuffer();

        // Parse the file using xlsx.js
        const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true  });

        // Get the first sheet from the workbook
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        // Convert the worksheet to JSON format (header: 1 creates arrays of arrays)
        const sheetData = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: false, dateNF: 'mm/dd/yyyy hh:mm:ss' });

        // Update the reactive state to store the Excel data
        excelData.value = sheetData;
    } catch (error) {
        toast.error('Error parsing the excel file');
        excelData.value = [];
    } finally {
        loading.value = false;
    }
}

function pdfInit(pdf) {
    if(pdf) {
        totalPagesPDF.value = pdf._pdfInfo.numPages;
    }
}

function isPdfReady() {
    isPDFLoaded.value = true;
}

function handlePdfPasswordRequest() {

}

const pdfWidth = computed(() => {
    // get the width of the pdf container based on the id pdfContainer and adjust return the width adjusted to the scale
    const elem = pdfContainer.value;

    if(elem) {
        const width = elem.offsetWidth;
        return `${width * (scalePDF.value / 100)}`;
    }
    return 0;
})

watch(showAllPages, () => {
    currentPagePDF.value = showAllPages.value ? null : 1
})

onMounted(async () => {
    if(documentType.value === 'word') {
        await fetchWord();
    } else if(documentType.value === 'excel') {
        await fetchAndParseXlsx();
    }
})
</script>